'use client';

/**
 * `<ContactSupportLink>` — drop-in CTA button rendered inside every
 * error fallback in the app (global-error, dash/error,
 * (with-chat)/error, per-route error.tsx files, SafeBoundary default
 * fallback, SectionFallback). Links to the PUBLIC `/support` route
 * (absolute URL via SITE_URL → relm.ai fallback) with a pre-filled
 * bug-report Question body containing the error digest,
 * property/portfolio IDs, the section name, and the source URL.
 *
 * The link targets the public `/support` (NOT `/dash/support`) so
 * the escape hatch works when the dash layout itself is crashing
 * (e.g. Clerk API returning 500 in `getSubscriptionState`). The
 * URL is absolute so even a partially-degraded local app can hand
 * the user a working production URL.
 *
 * The user can append additional notes inside the support modal's
 * Question field before submitting. Topic is locked to "Bug /
 * Performance" because every entry point is by definition a crash.
 *
 * Why this exists: the "Try again" buttons on error boundaries are
 * often useless because the upstream data is still in the same
 * broken state. Without a clear "Get help" path the user just
 * bounces. This component removes the friction of asking them to
 * copy/paste an error ID by hand.
 *
 * Read-only: this component MUST NOT crash even if context is
 * partial — it accepts every field as optional and falls back to a
 * still-usable URL when fields are missing. The link is rendered as
 * an `<a>` (not a Next.js `<Link>`) on purpose: error boundaries
 * sometimes mount with a broken router context, and a plain anchor
 * always works.
 */
import { Lifebuoy } from '@phosphor-icons/react';

import type { SupportErrorContext } from '@/helpers/supportErrorUrl';
import { buildSupportErrorUrl, safeSourceUrl } from '@/helpers/supportErrorUrl';

interface ContactSupportLinkProps extends SupportErrorContext {
  /** Visual variant — `primary` for solo CTAs, `subtle` when paired
   *  with a Try-again button. */
  variant?: 'primary' | 'subtle';
  /** Override the button label. */
  label?: string;
  /** Extra className appended to the rendered <a>. */
  className?: string;
}

export function ContactSupportLink({
  variant = 'subtle',
  label = 'Get help from support',
  className,
  errorId,
  errorMessage,
  propertyId,
  portfolioId,
  sectionName,
  sourceUrl,
}: ContactSupportLinkProps) {
  // Resolve `sourceUrl` lazily so SSR-rendered fallbacks don't crash
  // on missing `window`. Render-time eval is fine here because every
  // consumer is a Client Component error fallback.
  const url = buildSupportErrorUrl({
    errorId,
    errorMessage,
    propertyId,
    portfolioId,
    sectionName,
    sourceUrl: sourceUrl ?? safeSourceUrl(),
  });

  const baseClasses =
    'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-medium transition-colors';
  const variantClasses =
    variant === 'primary' ?
      'bg-brand-600 hover:bg-brand-700 text-white'
    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50';

  return (
    <a
      href={url}
      className={`${baseClasses} ${variantClasses}${className ? ` ${className}` : ''}`}
    >
      <Lifebuoy size={16} />
      {label}
    </a>
  );
}
